import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useApi } from '../ApiContext'; 
import './ViewOrder.css';

const ViewOrder = () => {
    const { api } = useApi();
    const history = useHistory();
    const location = useLocation();
    
    // --- API Endpoints ---
    const ORDERS_API = '/orders'; 
    const ORDER_DETAILS_API = (orderId) => `/orders/${orderId}`;
    const VERIFY_PAYMENT_API = (ref) => `/payment/verify/${ref}`;

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
    const [error, setError] = useState(null);

    const refreshOrderList = async () => {
        if (!api) return [];
        try {
            const response = await api.get(ORDERS_API);
            if (response.data.success) {
                const sortedOrders = response.data.orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setOrders(sortedOrders);
                return sortedOrders;
            }
        } catch (err) {
            console.error('Error refreshing order list:', err);
        }
        return [];
    };

    useEffect(() => {
        if (!api) return;

        const fetchOrders = async () => {
            setOrdersLoading(true);
            try {
                const response = await api.get(ORDERS_API);
                if (response.data.success) {
                    const sortedOrders = response.data.orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    setOrders(sortedOrders);
                } else {
                    throw new Error(response.data.error || 'Failed to fetch orders.');
                }
            } catch (err) {
                console.error('Error fetching orders:', err);
                setError(err.message || 'Could not load orders. Please try again.');
            } finally {
                setOrdersLoading(false);
            }
        };

        fetchOrders();
    }, [api]);

    useEffect(() => {
        if (ordersLoading || orders.length === 0) return;
        const params = new URLSearchParams(location.search);
        const orderIdParam = params.get('orderId');
        if (orderIdParam) {
            if (!selectedOrder || selectedOrder.order_id !== orderIdParam) {
                fetchOrderDetailsDirectly(orderIdParam);
            }
        }
    }, [location.search, ordersLoading]);

    const fetchOrderDetailsDirectly = async (orderId) => {
        setOrderDetailsLoading(true);
        setError(null);
        try {
            const currentOrderInList = orders.find(o => String(o.order_id) === String(orderId));
            const paymentReference = currentOrderInList?.bill_code || currentOrderInList?.transaction_id || orderId;
            const currentStatus = currentOrderInList?.order_status?.toLowerCase();

            if (currentOrderInList && (currentStatus === 'pending' || currentStatus === 'failed')) {
                try {
                    const verifyRes = await api.get(VERIFY_PAYMENT_API(paymentReference));
                    if (verifyRes.data.success && verifyRes.data.status === 'success') {
                        await refreshOrderList();
                    }
                } catch (vErr) {
                    console.error('Background payment verification failed:', vErr);
                }
            }

            const response = await api.get(ORDER_DETAILS_API(orderId));
            const data = response.data;

            if (data.success) {
                const groupedItemsMap = new Map();
                if (data.order.items) {
                    data.order.items.forEach(item => {
                        const existingProduct = groupedItemsMap.get(item.product_name);
                        const licensesWithDuration = (item.licenses || []).map(license => ({
                            ...license,
                            duration: item.duration 
                        }));
                        
                        if (existingProduct) {
                            existingProduct.licenses = existingProduct.licenses.concat(licensesWithDuration);
                        } else {
                            groupedItemsMap.set(item.product_name, {
                                product_name: item.product_name,
                                licenses: licensesWithDuration,
                            });
                        }
                    });
                }

                const groupedItemsArray = Array.from(groupedItemsMap.values());
                const finalBillCode = data.order.bill_code || currentOrderInList?.bill_code;

                setSelectedOrder({ 
                    ...data.order, 
                    bill_code: finalBillCode,
                    items: groupedItemsArray 
                });
            } else {
                throw new Error(data.error || 'Failed to fetch order details.');
            }
        } catch (err) {
            console.error('Error fetching order details:', err);
            setError(err.message || 'Could not load order details.');
            setSelectedOrder(null);
        } finally {
            setOrderDetailsLoading(false);
        }
    };

    const handleCardClick = (orderId) => {
        const currentOrderInList = orders.find(o => String(o.order_id) === String(orderId));
        setSelectedOrder({ 
            order_id: orderId, 
            bill_code: currentOrderInList?.bill_code,
            transaction_id: currentOrderInList?.transaction_id,
            order_status: currentOrderInList?.order_status,
            items: [] 
        });
        history.push(`/dashboard?page=viewOrder&orderId=${orderId}`);
        fetchOrderDetailsDirectly(orderId);
    };

    const handleCloseDetails = () => {
        setSelectedOrder(null);
        history.push('/dashboard?page=viewOrder');
    };

    // 🟢 Dapatkan kod rujukan pembayaran (bill_code / transaction_id / order_id)
    const handleRetryPayment = (order) => {
        const payRef = order.bill_code || order.transaction_id || order.order_id;
        if (payRef) {
            window.location.href = `https://toyyibpay.com/${payRef}`;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true };
        return new Date(dateString).toLocaleString(undefined, options);
    };

    if (ordersLoading) {
        return <div className="orders-loading-state"><div className="loading-message">Loading orders...</div></div>;
    }

    if (error && !selectedOrder) {
        return <div className="orders-error-state"><div className="error-message"><p>{error}</p></div></div>;
    }

    // 🟢 Semakan status & rujukan bayaran
    const normalizedStatus = selectedOrder?.order_status?.toLowerCase();
    const hasPaymentRef = Boolean(selectedOrder?.bill_code || selectedOrder?.transaction_id || selectedOrder?.order_id);
    const showRetryButton = (normalizedStatus === 'pending' || normalizedStatus === 'failed') && hasPaymentRef;

    return (
        <div className="view-orders-main">
            <div className="order-cards-grid">
                {orders.length > 0 ? (
                    orders.map(order => (
                        <div
                            key={order.order_id || order.id}
                            className={`order-card ${selectedOrder?.order_id === order.order_id ? 'order-card-selected' : ''}`}
                            onClick={() => handleCardClick(order.order_id)}
                        >
                            <div className="order-card-content">
                                <div className="order-header">
                                    <h4 className="order-id">Order #{order.order_id}</h4>
                                    <span className={`order-status ${order.order_status?.toLowerCase() === 'delivered' ? 'order-status-completed' : 'order-status-pending'}`}>
                                        {order.order_status}
                                    </span>
                                </div>
                                <div className="details-order">
                                    <p><span className="detail-label">Date:</span> {formatDate(order.created_at)}</p>
                                    <p><span className="detail-label">Total Value:</span> <span className="order-value">RM{order.value}</span></p>
                                    <p><span className="detail-label">Payment Method:</span> {order.payment_method}</p>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-orders-message">
                        <p className="text-xl">You have no orders yet.</p>
                        <p className="text-md">Start shopping to see your purchase history here!</p>
                    </div>
                )}
            </div>

            {selectedOrder && (
                <div className="order-details-overlay">
                    <div className="order-details-pane">
                        <div className="details-header">
                            <h4 className="details-title">Licenses for Order #{selectedOrder.order_id}</h4>
                            <button className="close-details-button" onClick={handleCloseDetails}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {orderDetailsLoading ? (
                            <div className="details-loading-state"><span>Checking & Loading details...</span></div>
                        ) : (
                            <div className="details-body">
                                {/* 🟢 Paparan butang bayar semula */}
                                {showRetryButton && (
                                    <div className="retry-payment-box">
                                        <p className="retry-payment-text">
                                            {normalizedStatus === 'failed'
                                                ? "Payment failed. Click below to try paying again."
                                                : "Payment is still pending. If you haven't paid yet, click below to complete your payment."}
                                        </p>
                                        <button 
                                            className="retry-payment-btn"
                                            onClick={() => handleRetryPayment(selectedOrder)}
                                        >
                                            Retry Payment (ToyyibPay)
                                        </button>
                                    </div>
                                )}

                                <ul className="license-list">
                                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                        selectedOrder.items.map((item, index) => (
                                            <li key={item.product_name || index} className="license-item">
                                                <h5 className="license-product-name">{item.product_name}</h5>
                                                <ul className="license-codes">
                                                    {item.licenses && item.licenses.length > 0 ? (
                                                        item.licenses.map((license, licenseIndex) => (
                                                            <li key={license.code || licenseIndex} className="license-code-item">
                                                                <div className="license-info">
                                                                    <span className="font-bold">Code:</span> <span className="break-all code-value">{license.code}</span>
                                                                </div>
                                                                <span className="license-duration">{license.duration}</span>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="no-licenses-message">No licenses found for this item.</li>
                                                    )}
                                                </ul>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="no-items-message">
                                            {normalizedStatus === 'pending' 
                                                ? "Payment cleared, but product keys are pending due to a temporary stock shortage. The system will automatically deploy them once inventory is replenished."
                                                : "No items found for this order."}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewOrder;